const BASE = '/api';

async function request(path, options = {}) {
    const { redirectOnUnauthorized = true, ...fetchOptions } = options;
    const token = localStorage.getItem('relay_token');
    const accountId = localStorage.getItem('relay_account_id');
    const isFormData = typeof FormData !== 'undefined' && fetchOptions.body instanceof FormData;
    const headers = { ...fetchOptions.headers };
    if (!isFormData && !headers['Content-Type']) headers['Content-Type'] = 'application/json';
    if (token) headers.Authorization = `Bearer ${token}`;
    if (accountId) headers['X-Account-Id'] = accountId;

    const res = await fetch(`${BASE}${path}`, { ...fetchOptions, headers });
    if (res.status === 401 && redirectOnUnauthorized) {
        localStorage.removeItem('relay_token');
        localStorage.removeItem('relay_account_id');
        window.location.href = '/login';
        throw new Error('Session expired');
    }
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `Request failed (${res.status})`);
    return data;
}

async function downloadFile(path, filename) {
    const token = localStorage.getItem('relay_token');
    const accountId = localStorage.getItem('relay_account_id');
    const headers = {};
    if (token) headers.Authorization = `Bearer ${token}`;
    if (accountId) headers['X-Account-Id'] = accountId;
    const res = await fetch(`${BASE}${path}`, { headers });
    if (!res.ok) throw new Error('Download failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename || 'export.csv';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
}

export default {
    // Auth
    login: (email, password) => request('/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
        redirectOnUnauthorized: false,
    }),
    me: () => request('/auth/me'),
    logout: () => { localStorage.removeItem('relay_token'); localStorage.removeItem('relay_account_id'); window.location.href = '/login'; },
    getConnectUrl: () => request('/auth/google/connect'),
    requestPasswordReset: (email) => request('/auth/password-reset', { method: 'POST', body: JSON.stringify({ email }) }),
    resetPassword: (token, password) => request('/auth/password-reset/confirm', { method: 'POST', body: JSON.stringify({ token, password }) }),

    // Accounts
    register: (email, password, name, account_name) => request('/accounts/register', { method: 'POST', body: JSON.stringify({ email, password, name, account_name }) }),
    getMyAccounts: () => request('/accounts/my-accounts'),
    createAccount: (name) => request('/accounts', { method: 'POST', body: JSON.stringify({ name }) }),
    getCurrentAccount: () => request('/accounts/current'),
    updateAccount: (data) => request('/accounts', { method: 'PATCH', body: JSON.stringify(data) }),
    getMembers: () => request('/accounts/members'),
    inviteUser: (email, role) => request('/accounts/invite', { method: 'POST', body: JSON.stringify({ email, role }) }),
    addUser: (email, password, name, role) => request('/accounts/add-user', { method: 'POST', body: JSON.stringify({ email, password, name, role }) }),
    getInvitations: () => request('/accounts/invitations'),
    revokeInvitation: (id) => request(`/accounts/invitations/${id}`, { method: 'DELETE' }),
    changeMemberRole: (userId, role) => request(`/accounts/members/${userId}/role`, { method: 'PATCH', body: JSON.stringify({ role }) }),
    removeMember: (userId) => request(`/accounts/members/${userId}`, { method: 'DELETE' }),
    acceptInvite: (token, password, name) => request('/accounts/accept-invite', { method: 'POST', body: JSON.stringify({ token, password, name }) }),
    switchAccount: (accountId) => { localStorage.setItem('relay_account_id', accountId); window.location.reload(); },

    // Workspaces
    getWorkspaces: () => request('/workspaces'),
    addSmtpWorkspace: (data) => request('/workspaces/smtp', { method: 'POST', body: JSON.stringify(data) }),
    addSendgridWorkspace: (data) => request('/workspaces/sendgrid', { method: 'POST', body: JSON.stringify(data) }),
    addMailgunWorkspace: (data) => request('/workspaces/mailgun', { method: 'POST', body: JSON.stringify(data) }),
    addResendWorkspace: (data) => request('/workspaces/resend', { method: 'POST', body: JSON.stringify(data) }),
    addSesWorkspace: (data) => request('/workspaces/ses', { method: 'POST', body: JSON.stringify(data) }),
    testWorkspace: (id) => request(`/workspaces/${id}/test`, { method: 'POST' }),
    deleteWorkspace: (id) => request(`/workspaces/${id}`, { method: 'DELETE' }),
    enableWarmup: (id) => request(`/workspaces/${id}/warmup`, { method: 'POST' }),
    activateWorkspace: (id, limit) => request(`/workspaces/${id}/activate`, { method: 'POST', body: JSON.stringify({ daily_send_limit: limit }) }),
    checkHealth: (id) => request(`/workspaces/${id}/health`, { method: 'POST' }),
    updateWorkspace: (id, data) => request(`/workspaces/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    uploadWorkspaceAvatar: (id, file) => {
        const formData = new FormData();
        formData.append('image', file);
        return request(`/workspaces/${id}/avatar`, { method: 'POST', body: formData });
    },
    removeWorkspaceAvatar: (id) => request(`/workspaces/${id}/avatar`, { method: 'DELETE' }),

    // Campaigns
    getCampaigns: (params) => request(`/campaigns${params ? '?' + new URLSearchParams(params) : ''}`),
    getCampaign: (id) => request(`/campaigns/${id}`),
    createCampaign: (data) => request('/campaigns', { method: 'POST', body: JSON.stringify(data) }),
    updateCampaign: (id, data) => request(`/campaigns/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteCampaign: (id) => request(`/campaigns/${id}`, { method: 'DELETE' }),
    launchCampaign: (id) => request(`/campaigns/${id}/launch`, { method: 'POST' }),
    pauseCampaign: (id) => request(`/campaigns/${id}/pause`, { method: 'POST' }),
    duplicateCampaign: (id) => request(`/campaigns/${id}/duplicate`, { method: 'POST' }),
    importCampaignHtml: (id, html) => request(`/campaigns/${id}/import-html`, { method: 'POST', body: JSON.stringify({ html }) }),
    previewCampaign: (id) => request(`/campaigns/${id}/preview`, { method: 'POST' }),
    preflightCampaign: (id) => request(`/campaigns/${id}/preflight`),
    testCampaign: (id, to_email, variant) => request(`/campaigns/${id}/test`, { method: 'POST', body: JSON.stringify({ to_email, variant }) }),
    retryCampaign: (id) => request(`/campaigns/${id}/retry`, { method: 'POST' }),
    exportCampaign: (id, name) => downloadFile(`/campaigns/${id}/export`, `${(name || 'campaign').replace(/[^a-z0-9]/gi, '_')}_results.csv`),
    getAbResults: (id) => request(`/campaigns/${id}/ab-results`),

    // Contacts
    getContacts: (params) => request(`/contacts${params ? '?' + new URLSearchParams(params) : ''}`),
    getContact: (id) => request(`/contacts/${id}`),
    createContact: (data) => request('/contacts', { method: 'POST', body: JSON.stringify(data) }),
    updateContact: (id, data) => request(`/contacts/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteContact: (id) => request(`/contacts/${id}`, { method: 'DELETE' }),
    bulkDeleteContacts: (ids) => request('/contacts/bulk-delete', { method: 'POST', body: JSON.stringify({ contact_ids: ids }) }),
    importContacts: (contacts, list_id) => request('/contacts/import', { method: 'POST', body: JSON.stringify({ contacts, list_id }) }),
    exportContacts: (params) => request(`/contacts/export${params ? '?' + new URLSearchParams(params) : ''}`),

    // Lists
    getLists: () => request('/contacts/lists'),
    getList: (id, params) => request(`/contacts/lists/${id}${params ? '?' + new URLSearchParams(params) : ''}`),
    createList: (name, description) => request('/contacts/lists', { method: 'POST', body: JSON.stringify({ name, description }) }),
    updateList: (id, data) => request(`/contacts/lists/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteList: (id) => request(`/contacts/lists/${id}`, { method: 'DELETE' }),
    addToList: (listId, contactIds) => request(`/contacts/lists/${listId}/add`, { method: 'POST', body: JSON.stringify({ contact_ids: contactIds }) }),
    removeFromList: (listId, contactIds) => request(`/contacts/lists/${listId}/remove`, { method: 'POST', body: JSON.stringify({ contact_ids: contactIds }) }),
    copyList: (id) => request(`/contacts/lists/${id}/copy`, { method: 'POST' }),

    // Suppressions
    getSuppressions: (params) => request(`/contacts/suppressions${params ? '?' + new URLSearchParams(params) : ''}`),
    addSuppression: (emails, reason) => request('/contacts/suppressions', { method: 'POST', body: JSON.stringify({ emails, reason }) }),
    removeSuppression: (id) => request(`/contacts/suppressions/${id}`, { method: 'DELETE' }),

    // Analytics
    getOverview: () => request('/analytics/overview'),
    getCampaignStats: (id) => request(`/analytics/campaign/${id}`),
    getWorkspaceAnalytics: () => request('/analytics/workspaces'),
    getUnsubscribeAnalytics: () => request('/analytics/unsubscribes'),
    getAudienceAnalytics: (params) => request(`/analytics/audience${params ? '?' + new URLSearchParams(params) : ''}`),
    getCampaignClicks: (id) => request(`/analytics/campaign/${id}/clicks`),
    getOperationsOverview: () => request('/operations/overview'),
    getOperationsTimeline: (params) => request(`/operations/timeline${params ? '?' + new URLSearchParams(params) : ''}`),
    getOperationsHistory: (days = 30) => request(`/operations/history?days=${days}`),
    getCampaignMap: () => request('/operations/campaign-map'),
    getInboxSchedule: (id) => request(`/operations/inboxes/${id}/schedule`),
    retrySend: (id) => request(`/operations/sends/${id}/retry`, { method: 'POST' }),
    cancelSend: (id) => request(`/operations/sends/${id}/cancel`, { method: 'POST' }),
    getScheduleProfiles: () => request('/operations/schedule-profiles'),
    createScheduleProfile: (data) => request('/operations/schedule-profiles', { method:'POST', body:JSON.stringify(data) }),
    updateScheduleProfile: (id,data) => request(`/operations/schedule-profiles/${id}`, { method:'PATCH', body:JSON.stringify(data) }),

    // Templates
    getTemplates: () => request('/templates'),
    getTemplate: (id) => request(`/templates/${id}`),
    createTemplate: (data) => request('/templates', { method: 'POST', body: JSON.stringify(data) }),
    updateTemplate: (id, data) => request(`/templates/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    deleteTemplate: (id) => request(`/templates/${id}`, { method: 'DELETE' }),
    saveAsTemplate: (campaignId, name) => request(`/templates/from-campaign/${campaignId}`, { method: 'POST', body: JSON.stringify({ name }) }),

    // Contact Verification
    verifyContacts: (options) => request('/contacts/verify', { method: 'POST', body: JSON.stringify(options || {}) }),
    getVerificationStats: () => request('/contacts/verification-stats'),

    // List Cleaning
    submitListCleaning: (emails) => request('/list-cleaning/submit', { method: 'POST', body: JSON.stringify({ emails }) }),
    getCleaningStatus: (jobId) => request(`/list-cleaning/status/${jobId}`),
    getCleaningResults: (jobId, filter) => request(`/list-cleaning/results/${jobId}?filter=${filter || 'all'}`),
    downloadCleanedCSV: (jobId, filter) => downloadFile(`/list-cleaning/csv/${jobId}?filter=${filter || 'valid'}`, `verified_${filter || 'valid'}.csv`),
    importCleanedList: (jobId, filter, listId, newListName) => request(`/list-cleaning/import/${jobId}`, { method: 'POST', body: JSON.stringify({ filter, list_id: listId, new_list_name: newListName }) }),

    // Settings
    getSettings: () => request('/settings'),
    updateSettings: (settings) => request('/settings', { method: 'POST', body: JSON.stringify(settings) }),

    // Spam Check
    checkSpam: (data) => request('/spam/check', { method: 'POST', body: JSON.stringify(data) }),
    deepScan: (data) => request('/spam/deep-scan', { method: 'POST', body: JSON.stringify(data) }),

    // Inbox Placement Testing
    getSeeds: () => request('/inbox-test/seeds'),
    getInboxTestRecipientLists: () => request('/inbox-test/recipient-lists'),
    addSeed: (data) => request('/inbox-test/seeds', { method: 'POST', body: JSON.stringify(data) }),
    deleteSeed: (id) => request(`/inbox-test/seeds/${id}`, { method: 'DELETE' }),
    runInboxTest: (data) => request('/inbox-test/run', { method: 'POST', body: JSON.stringify(data) }),
    getTestHistory: () => request('/inbox-test/history'),
    getInboxTestTracking: () => request('/inbox-test/tracking'),
};
